import styled from '@emotion/styled'
import {keyframes} from '@emotion/react'
import {theme} from '../Dashboard/theme'

const spin = keyframes`to { transform: rotate(360deg); }`

// Re-use nav + page shell from ClientProposalView — same background, same nav, same footer
export {
  PageWrap,
  NavBar,
  NavLeft,
  NavRight,
  NavForLabel,
  NavBusinessName,
  GradientSection,
  PageFooter,
  FooterText,
} from '../ClientProposalView/styled.components'

// Scheduler needs a flex column container so GradientSection can fill remaining height
export const Container = styled.div`
  margin: 0 auto;
  max-width: 1400px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`

// Title area sits on the gradient background, left-aligned like ClientProposalView's HeroSection
export const HeroArea = styled.div`
  padding: 30px 20px 20px;
  max-width: 820px;
  margin: auto;
`

export const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px;
  font-family: ${theme.font.family};
`

export const PageSubtitle = styled.p`
  font-size: 17px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
  line-height: 1.5;
  font-family: ${theme.font.family};

  strong {
    color: #ffffff;
    font-weight: 700;
  }
`

export const CardsSection = styled.div`
  flex: 1;
  padding: 30px 32px 40px;
  border-radius: 25px 25px 0 0;

  @media (max-width: 900px) {
    padding: 28px 20px;
  }

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`

// Outer wrapper that centres the white card and provides horizontal padding
export const CardOuter = styled.div`
  padding: 0 32px;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`

export const ScheduleCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  max-width: 820px;
  margin: 0 auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  font-family: ${theme.font.family};
`

export const CardBody = styled.div`
  padding: 24px 28px 28px;
`

export const SpanishRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`

export const SpanishLabel = styled.span`
  font-size: 13px;
  color: #374151;
  font-weight: 600;
  font-family: ${theme.font.family};
`

export const ToggleGroup = styled.div`
  display: flex;
  background: #e1eaff;
  border-radius: 8px;
  padding: 3px;
  gap: 3px;
  flex-shrink: 0;
`

export const ToggleBtn = styled.button`
  background: ${p => (p.active ? '#ffffff' : 'transparent')};
  padding: 5px 18px;
  border-radius: 6px;
  border: 1px solid ${p => (p.active ? '#0e30ac' : 'transparent')};
  color: ${p => (p.active ? '#0e30ac' : '#374151')};
  font-size: 13px;
  font-weight: ${p => (p.active ? '600' : '400')};
  cursor: pointer;
  font-family: ${theme.font.family};
  transition: background 0.15s, color 0.15s;
`

export const ScheduleColumns = styled.div`
  display: flex;
  gap: 28px;

  @media (max-width: 620px) {
    flex-direction: column;
    gap: 20px;
  }
`

export const ScheduleColumn = styled.div`
  flex: 1;
  min-width: 0;
`

export const ColumnLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  font-family: ${theme.font.family};
`

/* Time zone the appointment times are shown in — sits beside a ColumnLabel. */
export const TimeZoneTag = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  font-size: 11px;
  font-weight: 600;
  color: #3730a3;
  white-space: nowrap;
  font-family: ${theme.font.family};
`

export const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 2px;
`

export const SlotBtn = styled.button`
  padding: 9px 4px;
  border-radius: 6px;
  border: 1.5px solid
    ${p => {
      if (p.selected) return '#26325f'
      if (p.unavailable) return 'transparent'
      return '#e5e7eb'
    }};
  background: ${p => {
    if (p.unavailable) return '#f3f4f6'
    if (p.selected) return '#eef1f8'
    return '#ffffff'
  }};
  color: ${p =>
    p.unavailable ? '#9ca3af' : p.selected ? '#26325f' : '#374151'};
  font-size: 12px;
  cursor: ${p => (p.unavailable ? 'not-allowed' : 'pointer')};
  font-family: ${theme.font.family};
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: #26325f;
  }
`

export const SlotsEmpty = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 28px 0;
  color: #9ca3af;
  font-size: 13px;
`

export const SlotsLoading = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 0;
  color: #9ca3af;
  font-size: 13px;

  &::before {
    content: '';
    width: 24px;
    height: 24px;
    border: 3px solid #e5e7eb;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: ${spin} 0.75s linear infinite;
  }
`

export const ScheduledBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #15803d;
  font-weight: 500;
  margin-top: 8px;
`

export const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

export const ActionLeft = styled.div`
  display: flex;
  gap: 1px;
`

export const ActionRight = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    justify-content: flex-end;
  }
`

export const RegionRow = styled.div`
  margin-bottom: 20px;
`

export const RegionLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  font-family: ${theme.font.family};
`

export const RegionSelect = styled.select`
  width: 100%;
  max-width: 280px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  background: #ffffff;
  cursor: pointer;
  font-family: ${theme.font.family};

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`

/* ── react-calendar overrides ── */
export const CalendarWrap = styled.div`
  .react-calendar {
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-family: ${theme.font.family};
    font-size: 13px;
    color: #374151;
    overflow: hidden;
  }

  .react-calendar__navigation {
    display: flex;
    align-items: center;
    padding: 10px 8px 8px;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 0;
  }

  .react-calendar__navigation button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: #374151;
    padding: 4px 8px;
    border-radius: 4px;
    min-width: 28px;

    &:hover:not(:disabled) {
      background: #f3f4f6;
    }

    &:disabled {
      color: #d1d5db;
      cursor: not-allowed;
    }
  }

  .react-calendar__navigation__label {
    font-weight: 600;
    font-size: 14px;
    color: #111827;
    flex-grow: 1;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .react-calendar__month-view__weekdays {
    text-align: center;
    padding: 6px 0 2px;
  }

  .react-calendar__month-view__weekdays__weekday {
    color: #6b7280;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 0;
  }

  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
  }

  .react-calendar__tile {
    background: none;
    border: none;
    padding: 4px 0;
    cursor: pointer;
    font-family: ${theme.font.family};
    font-size: 13px;
    color: #374151;

    abbr {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin: 0 auto;
      transition: background 0.12s;
    }

    &:hover:not(:disabled) abbr {
      background: #f3f4f6;
    }

    &:disabled {
      color: #d1d5db;
      cursor: not-allowed;
    }
  }

  .react-calendar__tile--now abbr {
    background: #ff5000;
    color: #ffffff;
  }

  .react-calendar__tile--now:hover abbr {
    background: #e04500;
  }

  .react-calendar__tile--active abbr,
  .react-calendar__tile--active:hover abbr {
    background: #26325f !important;
    color: #ffffff !important;
  }

  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus,
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: transparent;
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: #d1d5db;
  }
`
